import { useEffect } from 'react';

import type { FireAnalyticsEvent } from '../../../../../../../../common/types';
import {
	useAnalytics,
	useCommonAndPrivateAttributes,
} from '../../../../../../../../controllers/analytics';

type UseExternalPersonFallbackAnalyticsArgs = {
	externalPersonFallback: boolean;
	integrationId?: string | null;
	hasThirdPartyUser: boolean;
	hasInitialContributors: boolean;
	hasAllContributors: boolean;
};

type BuildEventArgs = Omit<UseExternalPersonFallbackAnalyticsArgs, 'externalPersonFallback'> & {
	traceId?: string;
	searchSessionId: string;
};

/**
 * Builds the operational analytics event payload for the "External person" fallback case.
 */
const buildExternalPersonFallbackEvent = ({
	traceId,
	searchSessionId,
	integrationId,
	hasThirdPartyUser,
	hasInitialContributors,
	hasAllContributors,
}: BuildEventArgs): FireAnalyticsEvent => ({
	eventType: 'operational',
	action: 'taskFail',
	actionSubject: 'threadMessage',
	attributes: {
		commonAttributes: { traceId, searchSessionId },
		additionalAttributes: {
			task: 'resolveMessageSender',
			capability: 'threadMessage_externalPersonFallback',
			integrationId: integrationId ?? null,
			hasThirdPartyUser,
			hasInitialContributors,
			hasAllContributors,
		},
	},
});

/**
 * Fires a diagnostic operational analytics event when the "External person" fallback
 * label is shown in a ThreadMessage (i.e. no name or nickname could be resolved).
 */
export const useExternalPersonFallbackAnalytics = ({
	externalPersonFallback,
	integrationId,
	hasThirdPartyUser,
	hasInitialContributors,
	hasAllContributors,
}: UseExternalPersonFallbackAnalyticsArgs): void => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes } = useCommonAndPrivateAttributes();
	const { traceId, searchSessionId } = commonAttributes;

	useEffect(() => {
		if (!externalPersonFallback) {
			return;
		}
		fireAnalyticsEvent(
			buildExternalPersonFallbackEvent({
				traceId,
				searchSessionId,
				integrationId,
				hasThirdPartyUser,
				hasInitialContributors,
				hasAllContributors,
			}),
		);
	}, [
		externalPersonFallback,
		integrationId,
		hasThirdPartyUser,
		hasInitialContributors,
		hasAllContributors,
		traceId,
		searchSessionId,
		fireAnalyticsEvent,
	]);
};
